<template>
	<div class="static-content-page">
		<!-- definePageMeta -->
		<Head>
			<Title>{{ metaContent?.title ? ensureBrandSuffix(metaContent.title) : `${title} | CarFree` }}</Title>
			<Meta name="description" :content="metaContent?.description ?? ''" />
			<Meta v-if="config.public.isIndex" name="robots" :content="robotsCalculated" />
			<Meta v-else name="robots" content="noindex, nofollow" />
			<Link
				rel="canonical"
				:href="metaContent?.canonical && metaContent?.canonical?.trim() !== '' ? metaContent?.canonical : `${url.origin}${url.pathname}`" />
		</Head>

		<GlobalHeader />

		<div class="static-content-page__container">
			<h1 class="static-content-page__title">{{ title }}</h1>
			<CarRentalTextSection v-if="pageTextSection" class="static-content-page__content" />
			<slot />
		</div>
	</div>
</template>

<script setup lang="ts">
import { ECMSType, EPageSectionType } from '@/modules/adminModule/constants/pagesCMSconstants'

const props = defineProps<{
	// ECMSPage value, e.g. 'privacy-policy' — the CMS subpage the content is fetched from
	page: string
	// already translated heading, also used as the meta title fallback
	title: string
}>()

const config = useNuxtApp().$config
const url = useRequestURL()
const { locale } = useI18n()

// useAsyncData: fetched once on the server and read from the payload during hydration (no client refetch)
const { data: pageSections } = await useAsyncData<IHomePageSectionModel[] | null>(`static-page-sections-${props.page}-${locale.value}`, async () => {
	try {
		return (await ApiService.fetchPageSections(ECMSType.SUBPAGE, props.page, locale.value, { skipSSRPayload: true }))?.data ?? null
	} catch (error) {
		logger.log(error)
		return null
	}
})

const metaContent = computed<ICMSLocationContentTranslations | undefined>(
	() =>
		pageSections.value
			?.find((section) => section.name === EPageSectionType.META)
			?.translations.find((translation) => translation.language === locale.value)?.content[0] as unknown as
			| ICMSLocationContentTranslations
			| undefined,
)
const pageTextSection = computed(
	() => pageSections.value?.find((section) => section.name === EPageSectionType.CONTENT_SECTION) as unknown as ICMSLocationContent | undefined,
)

// CarRentalTextSection renders the `content-section` HTML from this injection
provide('pageTextSection', pageTextSection)

const robotsCalculated = useRobotsMeta(() => metaContent.value?.robots)
</script>

<style lang="scss" src="./StaticContentPage.scss"></style>
